import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BulkActions = ({
  selectedCount = 0,
  totalCount = 0,
  onSelectAll,
  onBulkAction,
  onCancel,
  isTrashView = false // New prop to determine if we're in trash view
}) => {
  const [confirmingAction, setConfirmingAction] = useState(null);

  const handleBulkAction = (action) => {
    if (action === 'delete' || action === 'restore' || action === 'permanent_delete') {
      setConfirmingAction(action);
      return;
    }
    
    onBulkAction?.(action);
  };

  const confirmAction = () => {
    onBulkAction?.(confirmingAction);
    setConfirmingAction(null);
  };

  const cancelAction = () => {
    setConfirmingAction(null);
  };

  const getActionText = (action) => {
    switch (action) {
      case 'delete': return 'Move to Trash';
      case 'restore': return 'Restore';
      case 'permanent_delete': return 'Permanently Delete';
      default: return action;
    }
  };

  const getActionDescription = (action) => {
    switch (action) {
      case 'delete': return 'QR codes will be moved to trash and can be restored later.';
      case 'restore': return 'QR codes will be restored and become active again.';
      case 'permanent_delete': return 'QR codes will be permanently deleted and cannot be restored.';
      default: return '';
    }
  };

  return (
    <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {/* Selection Info */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Icon name="CheckSquare" size={16} className="text-blue-600" />
            <span className="text-sm font-medium text-blue-900">
              {selectedCount} of {totalCount} selected
            </span>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={onSelectAll}
            className="text-blue-700 hover:text-blue-800"
          >
            {selectedCount === totalCount ? 'Deselect All' : 'Select All'}
          </Button>
        </div>
        
        {/* Bulk Actions */}
        <div className="flex items-center gap-2">
          {confirmingAction ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-red-600">
                Are you sure you want to {getActionText(confirmingAction).toLowerCase()} {selectedCount} QR code{selectedCount !== 1 ? 's' : ''}?
              </span>
              <p className="text-xs text-gray-600 mt-1">
                {getActionDescription(confirmingAction)}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={confirmAction}
                className="text-red-600 border-red-600 hover:bg-red-50"
              >
                Confirm {getActionText(confirmingAction)}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={cancelAction}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <>
              {selectedCount > 0 && (
                <>
                  {!isTrashView ? (
                    // Normal view actions
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleBulkAction('activate')}
                        className="flex items-center gap-2 text-green-700 border-green-300 hover:bg-green-50"
                      >
                        <Icon name="Play" size={14} />
                        Activate
                      </Button>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleBulkAction('deactivate')}
                        className="flex items-center gap-2 text-orange-700 border-orange-300 hover:bg-orange-50"
                      >
                        <Icon name="Pause" size={14} />
                        Deactivate
                      </Button>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleBulkAction('delete')}
                        className="flex items-center gap-2 text-red-700 border-red-300 hover:bg-red-50"
                      >
                        <Icon name="Trash2" size={14} />
                        Move to Trash
                      </Button>
                    </>
                  ) : (
                    // Trash view actions
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleBulkAction('restore')}
                        className="flex items-center gap-2 text-green-700 border-green-300 hover:bg-green-50"
                      >
                        <Icon name="RotateCcw" size={14} />
                        Restore
                      </Button>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleBulkAction('permanent_delete')}
                        className="flex items-center gap-2 text-red-700 border-red-300 hover:bg-red-50"
                      >
                        <Icon name="Trash2" size={14} />
                        Permanently Delete
                      </Button>
                    </>
                  )}
                </>
              )}
              
              <Button
                variant="ghost"
                size="sm"
                onClick={onCancel}
                className="text-gray-600"
              >
                Cancel
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkActions;